const io = require('socket.io-client');
const feathers = require('@feathersjs/client');
const socketio = require('@feathersjs/socketio-client');

import store from "Js/store";
import wsDebug from "Js/wsDebug";
import { createConnectivity } from "Js/connectivity";

const socket = io({
  transports: ["websocket"],
  query: { clientSessionId: wsDebug.clientSessionId }
});

// Telemetry: capture transport upgrades and pong latency from the engine layer.
try {
  const engine = socket.io && socket.io.engine;
  if (engine && typeof engine.on === 'function') {
    const initialTransport = engine.transport && engine.transport.name;
    if (initialTransport) wsDebug.stats.currentTransport = initialTransport;
    engine.on('upgrade', (transport) => {
      try {
        const name = (transport && transport.name) || (engine.transport && engine.transport.name) || null;
        wsDebug.record('upgrade', { transport: name });
      } catch (e) { /* noop */ }
    });
    engine.on('upgradeError', (err) => {
      try { wsDebug.record('upgradeError', { message: err && err.message, type: err && err.type }); }
      catch (e) { /* noop */ }
    });
    engine.on('pong', (ms) => {
      try { wsDebug.record('pong', { latencyMs: ms }); }
      catch (e) { /* noop */ }
    });
  }
} catch (e) { /* noop */ }

// Telemetry: browser-level visibility and online/offline hints.
try {
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', () => {
      try { wsDebug.record('visibility', { state: document.visibilityState }); }
      catch (e) { /* noop */ }
    });
  }
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('online', () => {
      try { wsDebug.record('online', { onLine: true }); } catch (e) { /* noop */ }
    });
    window.addEventListener('offline', () => {
      try { wsDebug.record('offline', { onLine: false }); } catch (e) { /* noop */ }
    });
  }
} catch (e) { /* noop */ }

const connectivity = createConnectivity({
  socket,
  win: typeof window !== 'undefined' ? window : null,
  wsDebug
});

connectivity.onTransition(({ to }) => {
  if (to === 'online') store.commit('SET_ISONLINE', true);
  else if (to === 'offline') store.commit('SET_ISONLINE', false);
});

const app = feathers();
app.configure(socketio(socket, {
  timeout: 20000
}));

// AUTHENTICATION
app.configure(
  feathers.authentication({
    header: "Authorization", // the default authorization header for REST
    path: "/authentication", // the server-side authentication service path
    jwtStrategy: "jwt", // the name of the JWT authentication strategy
    entity: "user", // the entity you are authenticating (ie. a users)
    service: "users", // the service to look up the entity
    idField: "id",

    cookie: "feathers-jwt", // the name of the cookie to parse the JWT from when cookies are enabled server side
    storageKey: "feathers-jwt", // the key to store the accessToken in localstorage or AsyncStorage on React Native
    storage: window.localStorage, // Passing a WebStorage-compatible object to enable automatic storage on the client.
    timeout: 20000
  })
);

const currentTransport = () => {
  try {
    return (socket.io && socket.io.engine && socket.io.engine.transport && socket.io.engine.transport.name) || null;
  } catch (e) { return null; }
};

socket.on("reconnect", (attempt) => {
  try { wsDebug.record('reconnect', { attempt, id: socket.id, transport: currentTransport() }); } catch (e) { /* noop */ }
  app.reAuthenticate();
});
socket.on("connect", function() {
  try { wsDebug.record('connect', { id: socket.id, transport: currentTransport() }); } catch (e) { /* noop */ }
});
socket.on("connecting", function() {
  try { wsDebug.record('connecting', { socketConnected: socket.connected }); } catch (e) { /* noop */ }
});
socket.on("connect_error", function(err) {
  try { wsDebug.record('connect_error', { message: err && err.message, type: err && err.type, socketConnected: socket.connected }); } catch (e) { /* noop */ }
});
socket.on("connect_failed", function() {
  try { wsDebug.record('connect_failed', { socketConnected: socket.connected }); } catch (e) { /* noop */ }
});
socket.on("close", function(reason) {
  try { wsDebug.record('close', { reason: reason || null }); } catch (e) { /* noop */ }
});
socket.on("disconnect", function(reason) {
  try { wsDebug.record('disconnect', { reason: reason || 'unknown', wasConnected: true }); } catch (e) { /* noop */ }
});
socket.on("reconnecting", function(attempt) {
  try { wsDebug.record('reconnecting', { attempt }); } catch (e) { /* noop */ }
});
socket.on("reconnect_attempt", function(attempt) {
  try { wsDebug.record('reconnect_attempt', { attempt }); } catch (e) { /* noop */ }
});
socket.on("reconnect_error", function(err) {
  try { wsDebug.record('reconnect_error', { message: err && err.message, type: err && err.type }); } catch (e) { /* noop */ }
});
socket.on("reconnect_failed", function() {
  try { wsDebug.record('reconnect_failed', {}); } catch (e) { /* noop */ }
});
socket.on("error", function(err) {
  try {
    wsDebug.record('error', {
      message: err && (err.message || String(err)),
      type: err && err.type,
      socketConnected: socket.connected
    });
  } catch (e) { /* noop */ }
  console.log("socket error");
});

app.service("script").timeout = 20000;
app.service("form").timeout = 20000;
app.service("user").timeout = 20000;
app.service("/users").timeout = 30000; // sign-up create also runs the onRegistration hook
app.service("authentication").timeout = 20000;

app.service("messages")
  .on('created', message => {

    store.dispatch('UPDATE_MODEL', {
      model: message.model,
      ind: message.modelIndex || 0
    });
    store.commit('SET_APP', message.app);

    store.dispatch("ACTIONS_QUEUE", message.actions);
  });

app.service("message/sendmessage")
  .on('created', message => {
    store.dispatch('UPDATE_MODEL', {
      model: message.model,
      ind: message.modelIndex || 0
    });
    store.commit('SET_APP', message.app);

    store.dispatch("ACTIONS_QUEUE", message.actions);
  });


const checkOnlineHook = async context => {
  const hookStartedAt = Date.now();
  const gateSnapshot = () => ({
    method: context && context.method,
    path: context && context.path,
    state: connectivity.getState(),
    storeOnline: (() => { try { return !!store.getters.isOnline(); } catch (_) { return null; } })(),
    navigatorOnLine: (() => { try { return !!navigator.onLine; } catch (_) { return null; } })(),
    socketConnected: (() => { try { return !!socket.connected; } catch (_) { return null; } })(),
  });

  try {
    await connectivity.waitForOnline();
    try { wsDebug.recordGate('resolved', Object.assign(gateSnapshot(), { waitedMs: Date.now() - hookStartedAt })); } catch (e) { /* noop */ }
  } catch (error) {
    try { wsDebug.recordGate('rejected', Object.assign(gateSnapshot(), { waitedMs: Date.now() - hookStartedAt, reason: error && error.message })); } catch (e) { /* noop */ }
    throw error;
  }

  return context;
};


// SURFACE SERVICES
export { app, socket, connectivity };

export const userService = app.service("users");
export const logService = app.service("logs");
export const formService = app.service("forms");
export const siteService = app.service("sites");

app.hooks({
  before: {
    all: [checkOnlineHook]
  }
})


export const scriptService = app.service('script');
export const messageService = app.service("messages");
export const messageAddUserService = app.service("message/adduser");
export const messageRemoveUserService = app.service("message/removeuser");
export const pushNotificationSend = app.service("pushdata/sendnotification");
export const pushDataGetKey = app.service("pushdata/vapidpublickey");
export const pushDataRegister = app.service("pushdata/register");
